/**
 * Jackpot Statistics API
 * Handles fetching jackpot statistics with demo mode support
 */

import { getJackpotStatsUrl } from '@repo/common';
import type IJackpotStat from '../../../types/models/IJackpotStat';
import type { IJackpotLevelStat } from '../../../types/models/IJackpotStat';

/** One jackpot instance exactly as the stats endpoint sends it. */
interface BeJackpotInstanceStat {
  instanceName: string;
  baseDenomination: number;
  levelStats: IJackpotLevelStat[];
  supportedCurrencies: string[];
  baseDenominationCurrencyMap: Record<string, number>;
  games?: string[];
}

export interface JackpotStatsResponse {
  jackpotInstancesStats: {
    instanceStats: BeJackpotInstanceStat[];
  };
}

/**
 * Add EGT currency jackpots by copying from the first available currency
 * (Ported from old lobby's APIIntegrationService)
 */
function addEGTCurrencyJackpots(jpInstances: BeJackpotInstanceStat[]): BeJackpotInstanceStat[] {
  jpInstances.forEach((instance) => {
    // Find the first available currency to copy from
    const firstLevel = instance.levelStats[0];
    if (!firstLevel || !firstLevel.currentValue || firstLevel.currentValue.length === 0) {
      return; // Skip if no current value data
    }

    const availableCurrencies = firstLevel.currentValue.map((vals) => vals.key);
    const selectedCurrency = availableCurrencies.sort()[0]; // Use first currency alphabetically

    // Add EGT currency data to each level
    instance.levelStats.forEach((level) => {
      // Add EGT to currentValue
      if (level.currentValue && level.currentValue.length > 0) {
        const sourceValue = level.currentValue.find((instVal) => instVal.key === selectedCurrency);
        if (sourceValue) {
          level.currentValue.push({
            ...sourceValue,
            key: 'EGT',
          });
        }
      }

      // Add EGT to boostValue
      if (level.boostValue && level.boostValue.length > 0) {
        const boostValue = level.boostValue.find((instVal) => instVal.key === selectedCurrency);
        if (boostValue) {
          level.boostValue.push({
            ...boostValue,
            key: 'EGT',
          });
        }
      }

      // Add EGT to lastWinValue
      if (level.lastWinValue && level.lastWinValue.length > 0) {
        const sourceValue = level.lastWinValue.find((instVal) => instVal.key === selectedCurrency);
        if (sourceValue) {
          level.lastWinValue.push({
            ...sourceValue,
            key: 'EGT',
          });
        }
      }

      // Add EGT to maxWinValue
      if (level.maxWinValue && level.maxWinValue.length > 0) {
        const sourceValue = level.maxWinValue.find((instVal) => instVal.key === selectedCurrency);
        if (sourceValue) {
          level.maxWinValue.push({
            ...sourceValue,
            key: 'EGT',
          });
        }
      }
    });

    // Add EGT to supported currencies
    if (!instance.supportedCurrencies.includes('EGT')) {
      instance.supportedCurrencies.push('EGT');
    }

    // Add EGT to denomination currency map
    if (instance.baseDenominationCurrencyMap[selectedCurrency] !== undefined) {
      instance.baseDenominationCurrencyMap['EGT'] = instance.baseDenominationCurrencyMap[selectedCurrency];
    }
  });

  return jpInstances;
}

/**
 * Parse jackpot response from API into IJackpotStat format
 * (Ported from old lobby's APIIntegrationService parseJackpotResponse function)
 */
function parseJackpotResponse(jackpotResponse: BeJackpotInstanceStat[], currency: string): Map<string, IJackpotStat> {
  const result = new Map<string, IJackpotStat>();

  jackpotResponse.forEach((jpStat) => {
    if (!result.has(jpStat.instanceName)) {
      result.set(jpStat.instanceName, {
        jackpotName: jpStat.instanceName,
        currency,
        currencies: jpStat.supportedCurrencies,
        levelStats: jpStat.levelStats,
        games: jpStat.games,
      });
    }
  });

  return result;
}

/**
 * Fetch jackpot statistics from API
 * Uses demo endpoint when currency is 'EGT'
 * @param mockedIncrement - Optional increment value for mocking jackpot animation (default: 0)
 */
export async function fetchJackpotStats(
  apiHost: string,
  currency: string,
  mockedIncrement: number = 0,
): Promise<Map<string, IJackpotStat>> {
  try {
    // Determine if we should use demo endpoint based on currency
    const isDemo = currency.toUpperCase() === 'EGT';

    // Use the common package function to construct the URL
    const url = getJackpotStatsUrl(apiHost, isDemo);


    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: JackpotStatsResponse = await response.json();

    // Parse response using the same logic as old lobby
    let instanceStats = data?.jackpotInstancesStats?.instanceStats || [];

    // Add EGT currency support (same as old lobby)
    instanceStats = addEGTCurrencyJackpots(instanceStats);

    const jackpotStats = parseJackpotResponse(instanceStats, currency);
    
    // Apply mock increment if provided (for testing animations)
    if (mockedIncrement > 0) {
      jackpotStats.forEach((stat) => {
        if (stat.levelStats) {
          stat.levelStats.forEach((level) => {
            if (level.currentValue) {
              level.currentValue.forEach((val) => {
                // Apply proportional increments based on level to create realistic behavior
                // Higher value jackpots (lower levelId) get bigger increments
                const baseValue = val.value || 0;
                let levelMultiplier: number;

                switch (level.levelId) {
                  case 1: // GRAND (highest value)
                    levelMultiplier = 5;
                    break;
                  case 2: // MAJOR
                    levelMultiplier = 3;
                    break;
                  case 3: // MINOR
                    levelMultiplier = 2;
                    break;
                  case 4: // MINI (lowest value)
                    levelMultiplier = 1;
                    break;
                  default:
                    levelMultiplier = 1;
                }

                // Add proportional increment to create realistic jackpot growth
                val.value = baseValue + (mockedIncrement * levelMultiplier);
              });
            }
          });
        }
      });
    }

    return jackpotStats;
  } catch (error) {
    console.error('[JackpotAPI] Failed to fetch jackpot statistics:', error);
    // Return empty map on error so the app continues to work
    return new Map<string, IJackpotStat>();
  }
}
